import React, { FC, Fragment, useEffect, useState } from 'react';
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { productionOverviewSnapshotTemplate } from 'apollo/templates/production-overview';
import {
    ProductionOverviewRowKey,
    ProductionOverviewSnapshotDepartment,
    ProductionOverviewSnapshotStep,
} from 'models/production-overview';
import Loader from 'components/shared/loader/loader.component';
import { OverviewCellClickHandler } from './overview-group-row.component';

type InProductionSectionProps = {
    clinicId?: string;
    onCellClick: OverviewCellClickHandler;
};

type SnapshotFlag = { isLate?: boolean; isPaused?: boolean };

const stepCells = (steps: ProductionOverviewSnapshotStep[], flag: SnapshotFlag) =>
    steps.map((step) => ({ rowKey: ProductionOverviewRowKey.SNAPSHOT_STEP, stepId: step.stepId, ...flag }));

const sumSteps = (steps: ProductionOverviewSnapshotStep[], field: keyof ProductionOverviewSnapshotStep): number =>
    steps.reduce((total, step) => total + Number(step[field] || 0), 0);

type SnapshotCountCellProps = { count: number; color?: string; onClick: () => void };

const SnapshotCountCell: FC<SnapshotCountCellProps> = ({ count, color, onClick }: SnapshotCountCellProps) => (
    <TableCell
        align="center"
        onClick={count > 0 ? onClick : undefined}
        sx={{ cursor: count > 0 ? 'pointer' : 'default' }}
    >
        {count > 0 ? (
            <Typography variant="body2" fontWeight={500} color={color}>
                {count}
            </Typography>
        ) : undefined}
    </TableCell>
);

const InProductionSection: FC<InProductionSectionProps> = ({ clinicId, onCellClick }: InProductionSectionProps) => {
    const { t: translate } = useTranslation(['production-overview', 'orders', 'workflow']);
    const [isExpanded, setIsExpanded] = useState(false);
    const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());

    const [loadSnapshot, { data, loading: isLoading, error, called: hasLoaded }] = useLazyQuery(
        productionOverviewSnapshotTemplate,
        { fetchPolicy: 'cache-and-network' },
    );

    // The snapshot is lazy: nothing is fetched until the section is first expanded. After
    // that, a clinic-filter change refreshes it (the filter narrows every number, DD-3800).
    useEffect(() => {
        if (hasLoaded) loadSnapshot({ variables: { clinicId } });
    }, [clinicId]);

    const handleToggleExpanded = () => {
        if (!isExpanded && !hasLoaded) loadSnapshot({ variables: { clinicId } });
        setIsExpanded(!isExpanded);
    };

    const toggleDepartment = (departmentName: string) => {
        const next = new Set(expandedDepartments);
        if (next.has(departmentName)) next.delete(departmentName);
        else next.add(departmentName);
        setExpandedDepartments(next);
    };

    const departments: ProductionOverviewSnapshotDepartment[] = data?.productionOverviewSnapshot || [];
    // The backend groups steps outside any department under null names, and orders outside
    // any step under the pseudo step NO_STEP with an empty name — both get readable labels.
    const departmentViews = departments.map((department) => ({
        key: department.departmentId || 'NO_DEPARTMENT',
        label: department.departmentName || translate('workflow:NO_DEPARTMENT'),
        steps: department.steps.map((step) => ({ ...step, label: step.stepName || translate('workflow:NO_STEP') })),
    }));
    const allSteps = departments.flatMap((department) => department.steps);
    const sectionLabel = translate('production-overview:IN_PRODUCTION_RIGHT_NOW');

    const handleSnapshotClick = (labelParts: string[], steps: ProductionOverviewSnapshotStep[], flag: SnapshotFlag) => {
        onCellClick([sectionLabel, ...labelParts], stepCells(steps, flag));
    };

    const headerTotals =
        hasLoaded && data ? (
            <Typography variant="body2" color="text.secondary">
                {translate('production-overview:SNAPSHOT_HEADER_TOTALS', {
                    orderCount: sumSteps(allSteps, 'orderCount'),
                    lateCount: sumSteps(allSteps, 'lateCount'),
                    pausedCount: sumSteps(allSteps, 'pausedCount'),
                })}
            </Typography>
        ) : undefined;

    return (
        <Paper sx={{ marginTop: 2, padding: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
                <IconButton
                    size="small"
                    onClick={handleToggleExpanded}
                    aria-label={sectionLabel}
                    data-test="production-overview.toggle-in-production"
                >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography variant="subtitle1" fontWeight={600}>
                    {sectionLabel}
                </Typography>
                {headerTotals}
                <Tooltip title={translate('production-overview:SNAPSHOT_NO_PLAN_TOOLTIP')}>
                    <InfoOutlinedIcon fontSize="small" color="disabled" />
                </Tooltip>
            </Box>

            {isExpanded ? (
                <Fragment>
                    {isLoading && !data ? <Loader /> : undefined}
                    {error ? (
                        <Typography variant="body2" color="error">
                            {translate('production-overview:SNAPSHOT_LOAD_ERROR')}
                        </Typography>
                    ) : undefined}
                    {data && !departments.length ? (
                        <Typography variant="body2" color="text.secondary" sx={{ padding: 1 }}>
                            {translate('production-overview:SNAPSHOT_EMPTY')}
                        </Typography>
                    ) : undefined}
                    {departments.length ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ minWidth: 220 }} />
                                        <TableCell align="center">
                                            {translate('production-overview:SNAPSHOT_COLUMN_COUNT')}
                                        </TableCell>
                                        <TableCell align="center">
                                            {translate('production-overview:SNAPSHOT_COLUMN_LATE')}
                                        </TableCell>
                                        <TableCell align="center">
                                            {translate('production-overview:SNAPSHOT_COLUMN_PAUSED')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {departmentViews.map((department) => (
                                        <Fragment key={department.key}>
                                            <TableRow data-test="production-overview.snapshot-department">
                                                <TableCell>
                                                    <Box display="flex" alignItems="center">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleDepartment(department.key)}
                                                            aria-label={department.label}
                                                        >
                                                            {expandedDepartments.has(department.key) ? (
                                                                <ExpandLessIcon fontSize="small" />
                                                            ) : (
                                                                <ExpandMoreIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {department.label}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <SnapshotCountCell
                                                    count={sumSteps(department.steps, 'orderCount')}
                                                    onClick={() =>
                                                        handleSnapshotClick([department.label], department.steps, {})
                                                    }
                                                />
                                                <SnapshotCountCell
                                                    count={sumSteps(department.steps, 'lateCount')}
                                                    color="#d32f2f"
                                                    onClick={() =>
                                                        handleSnapshotClick(
                                                            [department.label, translate('production-overview:LATE')],
                                                            department.steps,
                                                            { isLate: true },
                                                        )
                                                    }
                                                />
                                                <SnapshotCountCell
                                                    count={sumSteps(department.steps, 'pausedCount')}
                                                    color="#fb8c00"
                                                    onClick={() =>
                                                        handleSnapshotClick(
                                                            [department.label, translate('production-overview:PAUSED')],
                                                            department.steps,
                                                            { isPaused: true },
                                                        )
                                                    }
                                                />
                                            </TableRow>
                                            {expandedDepartments.has(department.key)
                                                ? department.steps.map((step) => (
                                                      <TableRow key={step.stepId}>
                                                          <TableCell sx={{ paddingLeft: 7 }}>
                                                              <Typography variant="body2">{step.label}</Typography>
                                                          </TableCell>
                                                          <SnapshotCountCell
                                                              count={step.orderCount}
                                                              onClick={() =>
                                                                  handleSnapshotClick(
                                                                      [department.label, step.label],
                                                                      [step],
                                                                      {},
                                                                  )
                                                              }
                                                          />
                                                          <SnapshotCountCell
                                                              count={step.lateCount}
                                                              color="#d32f2f"
                                                              onClick={() =>
                                                                  handleSnapshotClick(
                                                                      [
                                                                          department.label,
                                                                          step.label,
                                                                          translate('production-overview:LATE'),
                                                                      ],
                                                                      [step],
                                                                      { isLate: true },
                                                                  )
                                                              }
                                                          />
                                                          <SnapshotCountCell
                                                              count={step.pausedCount}
                                                              color="#fb8c00"
                                                              onClick={() =>
                                                                  handleSnapshotClick(
                                                                      [
                                                                          department.label,
                                                                          step.label,
                                                                          translate('production-overview:PAUSED'),
                                                                      ],
                                                                      [step],
                                                                      { isPaused: true },
                                                                  )
                                                              }
                                                          />
                                                      </TableRow>
                                                  ))
                                                : undefined}
                                        </Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : undefined}
                </Fragment>
            ) : undefined}
        </Paper>
    );
};

export default InProductionSection;
